import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Target,
  Clock,
  Menu,
  History,
  ChevronRight,
} from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import Loading from "../Loading";
import UserSidebar from "../UserSidebar";
import { getUserStats } from "../../services/TestService";
import { get } from "../../common/apiClient";

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [examHistory, setExamHistory] = useState([]);
  const [stats, setStats] = useState({
    coursesCount: 0,
    documentsCount: 0,
    examRoomsCount: 0,
    totalExams: 0,
    avgScore: 0,
    studyTime: 0
  });


  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsRes, historyRes] = await Promise.all([
          getUserStats(),
          get("/exam-result/history/all?page=1&limit=5"),
        ]);
        if (statsRes?.data) setStats(statsRes.data);
        setExamHistory(historyRes?.data || []);
      } catch (error) {
        const message = error?.response?.data?.message || "Có lỗi xảy ra";
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statsCards = [
    {
      title: "Tổng số bài thi",
      value: stats.totalExams,
      label: "Bài đã làm",
      icon: FileText,
      color: "text-red-600",
      bg: "bg-red-50"
    },
    {
      title: "Điểm trung bình",
      value: stats.avgScore,
      label: "Trung bình điểm tất cả các đề đã làm",
      icon: Target,
      color: "text-green-600",
      bg: "bg-green-50"
    },
    {
      title: "Thời gian học",
      value: `${stats.studyTime}h`,
      label: "Tổng thời gian",
      icon: Clock,
      color: "text-purple-600",
      bg: "bg-purple-50"
    }
  ];

  if (loading) return <Loading />;

  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <UserSidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      <main className="lg:ml-72 min-h-screen transition-all duration-300">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Mobile Header Toggle */}
            <div className="lg:hidden flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Tổng quan</h1>
              <button 
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-600"
              >
                <Menu size={24} />
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {statsCards.map((stat, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className={`p-3 rounded-xl ${stat.bg} ${stat.color}`}>
                      <stat.icon size={24} />
                    </div>
                    <span className="text-3xl font-bold text-gray-900">{stat.value}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{stat.title}</h3>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Notifications Section */}
            {/* Exam History Section */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <History size={20} className="text-red-600" />
                  Lịch sử làm bài gần đây
                </h3>
                <button
                  onClick={() => navigate("/exam-history")}
                  className="flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
                >
                  Xem tất cả <ChevronRight size={16} />
                </button>
              </div>

              {examHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                  <History size={48} className="mb-4 opacity-20" />
                  <p>Bạn chưa làm bài thi nào.</p>
                  <button
                    onClick={() => navigate("/exam")}
                    className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                  >
                    Khám phá đề thi
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                        <th className="p-4 font-semibold">#</th>
                        <th className="p-4 font-semibold">Đề thi</th>
                        <th className="p-4 font-semibold hidden md:table-cell">Ngày nộp</th>
                        <th className="p-4 font-semibold text-center">Điểm</th>
                        <th className="p-4 font-semibold text-center hidden md:table-cell">Thời gian làm</th>
                        <th className="p-4 font-semibold text-right">Chi tiết</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {examHistory.map((item, index) => {
                        const exam = item.examId || {};
                        const score = item.total_score ?? item.totalScore ?? item.score;
                        const type = String(exam.type || "").toUpperCase();
                        const maxScore = type === "D4" ? 10
                          : (type === "HSK1" || type === "HSK2") ? 200
                          : type.startsWith("HSK") ? 300
                          : null;
                        const durationInSeconds = item.examCompledTime || 0;
                        const minutes = Math.floor(durationInSeconds / 60);
                        const seconds = durationInSeconds % 60;
                        return (
                          <tr key={item._id} className="hover:bg-gray-50 transition-colors group">
                            <td className="p-4 text-sm font-medium text-gray-400">{index + 1}</td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-red-50 text-red-600 group-hover:bg-white group-hover:shadow-sm transition">
                                  <FileText className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm line-clamp-1 max-w-[160px] md:max-w-xs">
                                    {typeof exam.title === "object"
                                      ? exam.title?.text || exam.title?.code || "Đề thi không xác định"
                                      : exam.title || "Đề thi không xác định"}
                                  </p>
                                  <p className="text-xs text-gray-400 bg-gray-100 inline-block px-1.5 py-0.5 rounded mt-0.5">
                                    {exam.type || "N/A"}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 hidden md:table-cell">
                              <div className="text-sm">
                                <p className="font-medium text-gray-900">{moment(item.createdAt).format("DD/MM/YYYY")}</p>
                                <p className="text-xs text-gray-400">{moment(item.createdAt).format("HH:mm")}</p>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <span className={`inline-block px-3 py-1 rounded-lg font-bold text-sm
                                ${Number(score || 0) >= 8 ? "bg-green-100 text-green-700"
                                  : Number(score || 0) >= 5 ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-50 text-red-600"}`}>
                                {score ?? "N/A"}
                                {maxScore != null && (
                                  <span className="text-xs font-normal text-gray-400"> / {maxScore}</span>
                                )}
                              </span>
                            </td>
                            <td className="p-4 text-center hidden md:table-cell">
                              <div className="flex items-center justify-center gap-1 text-gray-500 text-sm">
                                <Clock className="w-4 h-4" />
                                {minutes}p {seconds}s
                              </div>
                            </td>
                            <td className="p-4 text-right">
                              <button
                                onClick={() => navigate(`/exam/result/${item.examId?._id || item.examId}?resultId=${item._id}`)}
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 font-medium transition-colors text-sm"
                              >
                                Xem lại <ChevronRight className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
